import React, { FC, useCallback, useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditSection from "components/common/ui/EditSection";
import CommonButton from "components/common/ui/CommonButton";
import { SpacedDatePicker } from "components/common/ui/SpacedTextInput";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import { menusInWeekActions } from "store/slices/menu/menusInWeekSlice";
import currentSiteSelector from "store/selectors/userGroup/currentSiteSelector";
import { foodPurchasingGuideActions } from "store/slices/inventory/foodPurchasingGuideSlice";

/**
 * Validate date range
 * @param from - from date
 * @param to - to date
 * @returns error message if date range is invalid, otherwise empty string
 */
const validateDateRange = (from: Dayjs | null, to: Dayjs | null): string => {
  if (!from || !to) {
    return "";
  }
  if (to.isBefore(from)) {
    return '"To" date cannot be earlier than "From" date.';
  }
  if (to.diff(from, "day") >= 7) {
    return "Date range cannot exceed 7 days.";
  }
  return "";
};

interface ChooseDateRangeStepProps {
  onNext: (from: Dayjs, to: Dayjs) => void;
  onBack: () => void;
}

const ChooseDateRangeStep: FC<ChooseDateRangeStepProps> = ({
  onNext,
  onBack,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const currentSiteId = useAppSelector(currentSiteSelector.currentSiteId);

  const [fromDate, setFromDate] = useState<Dayjs | null>(
    searchParams.get("dateFrom") ? dayjs(searchParams.get("dateFrom")) : null,
  );
  const [toDate, setToDate] = useState<Dayjs | null>(
    searchParams.get("dateTo") ? dayjs(searchParams.get("dateTo")) : null,
  );
  const [error, setError] = useState<string>("");

  /**
   * Make sure to validate date range when fromDate or toDate changes
   */
  useEffect(() => {
    setError(validateDateRange(fromDate, toDate));
  }, [fromDate, toDate]);

  /**
   * Handle start/end date changes
   */
  const handleDateChange = useCallback(
    (dateName: string) => (date: Dayjs | null) => {
      if (dateName === "dateTo") {
        setToDate(date);
        setError(validateDateRange(fromDate, date));
      } else {
        setFromDate(date);
        setError(validateDateRange(date, toDate));
      }
      if (date) {
        searchParams.set(dateName, date.format("YYYY-MM-DD"));
      } else {
        searchParams.delete(dateName);
      }
      setSearchParams(searchParams, { replace: true });
    },
    [fromDate, searchParams, setSearchParams, toDate],
  );

  /**
   * Handle next button click
   */
  const handleContinue = (): void => {
    if (!fromDate || !toDate) {
      setError("Please select both dates.");
      return;
    }
    const validation = validateDateRange(fromDate, toDate);
    if (validation) {
      setError(validation);
      return;
    }
    setError("");
    dispatch(foodPurchasingGuideActions.setFoodPurchasingGuide(null));
    dispatch(
      menusInWeekActions.fetchMenusInWeek({
        startDate: fromDate,
        endDate: toDate,
        menuType: "cycling menu",
        forSiteId: currentSiteId,
      }),
    );
    onNext(fromDate, toDate);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography>
          Select the date range for ingredient purchase list (max 7 days):
        </Typography>
        <EditSection>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SpacedDatePicker
                label="From Date"
                value={fromDate}
                onChange={handleDateChange("dateFrom")}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    helperText: error && fromDate && toDate ? error : undefined,
                    error: !!(error && fromDate && toDate),
                  },
                  field: {
                    clearable: !!fromDate,
                    onClear: () => {
                      handleDateChange("dateFrom")(null);
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SpacedDatePicker
                label="To Date"
                value={toDate}
                onChange={handleDateChange("dateTo")}
                minDate={fromDate || undefined}
                maxDate={fromDate ? fromDate.add(6, "day") : undefined}
                slotProps={{
                  textField: {
                    helperText: error && fromDate && toDate ? error : undefined,
                    error: !!(error && fromDate && toDate),
                  },
                  field: {
                    clearable: !!toDate,
                    onClear: () => {
                      handleDateChange("dateTo")(null);
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item>
              <CommonButton
                text="Continue"
                onClick={handleContinue}
                positive={!(!fromDate || !toDate || !!error)}
                disabled={!fromDate || !toDate || !!error}
              />
            </Grid>
            <Grid item>
              <CommonButton text="Back" onClick={onBack} />
            </Grid>
          </Grid>
        </EditSection>
      </Box>
    </LocalizationProvider>
  );
};

export default ChooseDateRangeStep;
